import { Injectable } from '@angular/core';
import { Core_URL } from '../../auth/config/constants';
import { StorageService } from '../../core/helpers/storage.service';
import { Storagekeys } from '../../core/model/storage';
import {
  CallDiagnosticDocument,
  DiagnosticCallType,
  DiagnosticEvent,
  DiagnosticMediaSession,
  DiagnosticMetricWindow,
  DiagnosticPreCallEvent,
  DiagnosticRtpSample,
  DiagnosticSeverity,
} from '../models/call-diagnostics';

interface StartCallContext {
  callId: string;
  callType: DiagnosticCallType;
  sipCallId?: string | null;
  tenantId?: string | null;
  agentId?: string | null;
}

interface ObservableRtcSession {
  connection?: RTCPeerConnection;
  on(eventName: string, listener: (event: unknown) => void): void;
}

interface AudioRtcStats extends RTCStats {
  kind?: string;
  mediaType?: string;
  state?: string;
  nominated?: boolean;
  selected?: boolean;
  selectedCandidatePairId?: string;
  bytesReceived?: number;
  packetsReceived?: number;
  packetsLost?: number;
  jitter?: number;
  audioLevel?: number;
  totalAudioEnergy?: number;
  bytesSent?: number;
  packetsSent?: number;
  roundTripTime?: number;
  currentRoundTripTime?: number;
  ssrc?: number;
}

interface BufferedPreCallEvent {
  eventId: string;
  type: string;
  timestamp: string;
  timestampEpochMs: number;
  mediaSessionId: string | null;
  severity: DiagnosticSeverity;
  data: Record<string, unknown>;
}

interface MediaInterruption {
  mediaSessionId: string | null;
  startedAt: string;
  startedEpochMs: number;
  source: string;
  state: string;
  suspectedDeadAirRecorded: boolean;
}

interface RtpProgressState {
  lastBytes: number | null;
  lastPackets: number | null;
  lastOffsetMs: number | null;
  stalledSinceOffsetMs: number | null;
  gapRecorded: boolean;
}

interface CallDiagnosticsUpsertRequest {
  schemaVersion: number;
  checkpointSequence: number;
  diagnosticId: string;
  callId: string;
  sipCallId: string | null;
  callType: DiagnosticCallType;
  agentId: string | null;
  clientSessionId: string;
  browserTabId: string;
  mediaSessionId: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  isFinal: boolean;
  transferCallId: string | null;
  transferCount: number;
  deadAirDetected: boolean;
  deadAirIncidentCount: number;
  payload: Omit<
    CallDiagnosticDocument,
    'uploadState' | 'uploadStatus' | 'retentionExpiresAt'
  >;
}

@Injectable({ providedIn: 'root' })
export class CallDiagnosticsService {
  private readonly databaseName = 'kunnect-call-diagnostics';
  private readonly storeName = 'calls';
  private readonly tenantUploadIndexName = 'tenantUploadStatus';
  private readonly retentionIndexName = 'retentionExpiresAt';
  private readonly clientSessionStorageKey = 'call_diagnostics_client_session';
  private readonly browserTabId = this.uuid();
  private readonly observedSessions = new WeakSet<object>();
  private readonly observedPeerConnections = new WeakSet<RTCPeerConnection>();
  private readonly peerConnectionMediaSessionIds = new WeakMap<
    RTCPeerConnection,
    string
  >();
  private readonly rtcSessionMediaSessionIds = new WeakMap<object, string>();
  private readonly observedAudioElements = new WeakSet<HTMLAudioElement>();
  private readonly observedTracks = new WeakSet<MediaStreamTrack>();
  private readonly observedAudioContexts = new WeakSet<AudioContext>();
  private readonly rollingSampleLimit = 30;
  private readonly detailedCollectionAfterIncidentMs = 60_000;
  private readonly persistIntervalMs = 30_000;
  private readonly metricWindowMs = 10_000;
  private readonly incidentDeduplicationMs = 10_000;
  private readonly suspectedDeadAirThresholdMs = 8_000;
  private readonly preCallWindowMs = 5 * 60_000;
  private readonly preCallEventLimit = 100;
  private readonly eventLimit = 1_000;
  private readonly metricWindowLimit = 1_800;
  private readonly incidentSampleLimit = 600;
  private readonly uploadIntervalMs = 60_000;
  private readonly uploadBatchLimit = 10;
  private readonly uploadTimeoutMs = 8_000;
  private readonly maxKeepaliveBodyBytes = 60_000;
  private readonly retentionMs = 28 * 60 * 60_000;
  private readonly cleanupIntervalMs = 60 * 60_000;

  private activeDocument: CallDiagnosticDocument | null = null;
  private callStartedEpochMs = 0;
  private rollingSamples: DiagnosticRtpSample[] = [];
  private windowSamples: DiagnosticRtpSample[] = [];
  private detailedUntilEpochMs = 0;
  private persistTimer?: ReturnType<typeof setInterval>;
  private databasePromise?: Promise<IDBDatabase | null>;
  private lastCandidatePairId: string | null = null;
  private lastInboundSsrc: number | null = null;
  private lastOutboundSsrc: number | null = null;
  private mediaSessionId: string | null = null;
  private mediaInterruption: MediaInterruption | null = null;
  private mediaInterruptionTimer?: ReturnType<typeof setTimeout>;
  private inboundProgress = this.emptyRtpProgressState();
  private outboundProgress = this.emptyRtpProgressState();
  private readonly lastIncidentEpochMs = new Map<string, number>();
  private documentVersion = 0;
  private readonly persistedVersions = new Map<string, number>();
  private persistenceScheduled = false;
  private preCallBuffer: BufferedPreCallEvent[] = [];
  private uploadTimer?: ReturnType<typeof setInterval>;
  private cleanupTimer?: ReturnType<typeof setInterval>;
  private uploadScheduled = false;
  private uploadInProgress = false;
  private uploadRequestedAfterCurrent = false;
  private keepaliveRequested = false;
  private claimedOwnershipContext: string | null = null;
  private cachedClientSessionId: string | null = null;

  constructor(private readonly storage: StorageService) {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.record(
          'DOCUMENT_VISIBILITY_CHANGED',
          { state: document.visibilityState },
          document.visibilityState === 'hidden' ? 'warning' : 'info'
        );
        if (document.visibilityState === 'hidden') {
          this.persistActiveDocument(true);
        }
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flushForLogout());
      window.addEventListener('online', () => {
        this.record('BROWSER_NETWORK_ONLINE');
      });
      window.addEventListener('offline', () => {
        this.record('BROWSER_NETWORK_OFFLINE', {}, 'warning');
        this.beginMediaInterruption(
          this.mediaSessionId,
          'BROWSER_NETWORK_STATE',
          'offline'
        );
      });
      this.uploadTimer = setInterval(
        () => this.scheduleUpload(),
        this.uploadIntervalMs
      );
      setTimeout(() => this.scheduleUpload(), 5_000);
      this.cleanupTimer = setInterval(
        () => this.scheduleExpiredDocumentCleanup(),
        this.cleanupIntervalMs
      );
      setTimeout(() => this.scheduleExpiredDocumentCleanup(), 10_000);
    }
  }

  flushForLogout(): void {
    try {
      this.persistActiveDocument(true);
      this.scheduleUpload(true);
    } catch {
      // Logout and navigation must never wait for diagnostics.
    }
  }

  resumeUploads(): void {
    this.runSafely(() => this.scheduleUpload());
  }

  startCall(context: StartCallContext): void {
    this.runSafely(() => this.startCallInternal(context));
  }

  private startCallInternal(context: StartCallContext): void {
    if (this.activeDocument?.callId === context.callId) {
      return;
    }

    if (this.activeDocument && !this.activeDocument.isFinal) {
      this.finalizeCall('REPLACED_BY_NEW_CALL');
    }

    const callStartEpochMs = Date.now();
    const now = new Date(callStartEpochMs).toISOString();
    const clientSessionId = this.getClientSessionId();
    const tenantId =
      this.nonEmptyString(context.tenantId) ??
      this.getStoredString(Storagekeys.orgId);
    const agentId =
      this.nonEmptyString(context.agentId) ??
      this.getStoredString(Storagekeys.userId);
    const preCallEvents = this.consumePreCallEvents(callStartEpochMs);
    this.callStartedEpochMs = callStartEpochMs;
    this.rollingSamples = [];
    this.windowSamples = [];
    this.detailedUntilEpochMs = 0;
    this.lastCandidatePairId = null;
    this.lastInboundSsrc = null;
    this.lastOutboundSsrc = null;
    this.resetRtpProgress();
    this.clearMediaInterruption();
    this.lastIncidentEpochMs.clear();
    this.documentVersion = 0;

    this.activeDocument = {
      schemaVersion: 2,
      checkpointSequence: 0,
      uploadStatus: 'PENDING',
      diagnosticId: `${tenantId ?? 'unknown'}:${clientSessionId}:${this.browserTabId}:${context.callId}`,
      clientSessionId,
      browserTabId: this.browserTabId,
      mediaSessionId: this.mediaSessionId,
      mediaSessions: this.mediaSessionId
        ? [this.createMediaSession(this.mediaSessionId, null, true)]
        : [],
      tenantId,
      agentId,
      callId: context.callId,
      sipCallId: context.sipCallId ?? null,
      callType: context.callType,
      startedAt: now,
      answeredAt: null,
      endedAt: null,
      isFinal: false,
      currentPhase: 'PRIMARY',
      transferCallId: null,
      transfers: [],
      preCallEvents,
      events: [],
      metricWindows: [],
      incidentSamples: [],
      uploadState: {
        lastUploadedCheckpointSequence: -1,
        attemptCount: 0,
        lastAttemptAt: null,
        nextAttemptAt: null,
      },
      retentionExpiresAt: new Date(
        callStartEpochMs + this.retentionMs
      ).toISOString(),
      updatedAt: now,
    };

    this.record('CALL_DIAGNOSTICS_STARTED', {
      callType: context.callType,
      preCallEventCount: preCallEvents.length,
      browserOnline: typeof navigator === 'undefined' ? null : navigator.onLine,
      documentVisibility:
        typeof document === 'undefined' ? null : document.visibilityState,
    });
    this.startPersistenceTimer();
    this.schedulePersistence();
  }

  markAnswered(): void {
    this.runSafely(() => this.markAnsweredInternal());
  }

  private markAnsweredInternal(): void {
    if (!this.activeDocument || this.activeDocument.answeredAt) return;
    this.activeDocument.answeredAt = new Date().toISOString();
    this.resetRtpProgress();
    this.record('CALL_ANSWERED');
  }

  markTransferAnswered(transferCallId: string | null): void {
    this.runSafely(() => this.markTransferAnsweredInternal(transferCallId));
  }

  private markTransferAnsweredInternal(transferCallId: string | null): void {
    if (!this.activeDocument) return;
    let transfer = [...this.activeDocument.transfers]
      .reverse()
      .find(candidate =>
        transferCallId
          ? candidate.callId === transferCallId
          : !candidate.completedAt
      );
    if (transfer?.completedAt || transfer?.status === 'ANSWERED') return;
    if (!transfer) {
      this.startTransferInternal(transferCallId);
      transfer = this.activeDocument.transfers.at(-1);
    }
    if (!transfer) return;
    transfer.status = 'ANSWERED';
    this.record(
      'TRANSFER_ANSWERED',
      {},
      'info',
      transferCallId ?? transfer.callId
    );
  }

  startTransfer(transferCallId: string | null): void {
    this.runSafely(() => this.startTransferInternal(transferCallId));
  }

  private startTransferInternal(transferCallId: string | null): void {
    if (!this.activeDocument) return;
    if (this.activeDocument.currentPhase === 'TRANSFER') {
      if (
        !transferCallId ||
        this.activeDocument.transferCallId === transferCallId
      ) {
        return;
      }
      if (!this.activeDocument.transferCallId) {
        this.activeDocument.transferCallId = transferCallId;
        const activeTransfer = this.activeDocument.transfers.at(-1);
        if (activeTransfer) activeTransfer.callId = transferCallId;
        this.record('TRANSFER_CALL_ID_ASSIGNED', {}, 'info', transferCallId);
        return;
      }
    }
    this.activeDocument.currentPhase = 'TRANSFER';
    this.activeDocument.transferCallId = transferCallId;
    this.activeDocument.transfers.push({
      callId: transferCallId,
      startedAt: new Date().toISOString(),
      completedAt: null,
      status: 'STARTED',
    });
    this.record('TRANSFER_STARTED', {}, 'info', transferCallId);
  }

  completeTransfer(status: string): void {
    this.runSafely(() => this.completeTransferInternal(status));
  }

  private completeTransferInternal(status: string): void {
    if (!this.activeDocument) return;
    const transfer = this.activeDocument.transfers.at(-1);
    if (transfer) {
      transfer.status = status;
      transfer.completedAt = new Date().toISOString();
    }
    this.record(
      `TRANSFER_${status.replace(/\s+/g, '_').toUpperCase()}`,
      {},
      status === 'COMPLETED' ? 'info' : 'warning',
      this.activeDocument.transferCallId
    );
    this.activeDocument.currentPhase = 'PRIMARY';
  }

  finalizeCall(reason: string): void {
    this.runSafely(() => this.finalizeCallInternal(reason));
  }

  private finalizeCallInternal(reason: string): void {
    if (!this.activeDocument || this.activeDocument.isFinal) return;
    this.closeOpenTransfers(reason);
    this.flushMetricWindow();
    this.emitSuspectedConnectionDeadAir();
    this.closeOpenMediaSession(reason);
    this.record('CALL_DIAGNOSTICS_FINALIZED', { reason });
    this.activeDocument.endedAt = new Date().toISOString();
    this.activeDocument.isFinal = true;
    this.persistActiveDocument(true);
    this.stopPersistenceTimer();
    this.activeDocument = null;
    this.rollingSamples = [];
    this.windowSamples = [];
    this.resetRtpProgress();
    this.clearMediaInterruption();
  }

  private closeOpenTransfers(reason: string): void {
    if (!this.activeDocument) return;
    const transfers = this.activeDocument.transfers;
    for (const [index, transfer] of transfers.entries()) {
      if (transfer.completedAt) continue;
      transfer.completedAt = new Date().toISOString();
      if (reason === 'TRANSFER_COMPLETED' && index === transfers.length - 1) {
        transfer.status = 'COMPLETED';
      } else if (transfer.status === 'STARTED') {
        transfer.status = 'INTERRUPTED';
      }
      this.record(
        'TRANSFER_CLOSED_ON_CALL_FINALIZATION',
        { reason, status: transfer.status },
        transfer.status === 'INTERRUPTED' ? 'warning' : 'info',
        transfer.callId
      );
    }
  }

  record(
    type: string,
    data: Record<string, unknown> = {},
    severity: DiagnosticSeverity = 'info',
    relatedCallId?: string | null,
    mediaSessionId: string | null = this.mediaSessionId
  ): void {
    try {
      const document = this.activeDocument;
      if (!document) {
        this.bufferPreCallEvent(type, data, severity, mediaSessionId);
        return;
      }

      const event: DiagnosticEvent = {
        eventId: this.uuid(),
        type,
        timestamp: new Date().toISOString(),
        offsetMs: Math.max(0, Date.now() - this.callStartedEpochMs),
        mediaSessionId,
        phase: document.currentPhase,
        relatedCallId:
          relatedCallId ??
          (document.currentPhase === 'TRANSFER'
            ? document.transferCallId
            : null),
        severity,
        data: this.sanitizeData(data),
      };
      document.events.push(event);
      if (document.events.length > this.eventLimit) {
        // Preserve the call-start event and retain the newest diagnostic data.
        document.events.splice(1, document.events.length - this.eventLimit);
      }
      document.updatedAt = event.timestamp;
      this.documentVersion++;
    } catch {
      // All diagnostics operations are intentionally non-blocking and fail-safe.
    }
  }

  recordError(type: string, error: unknown): void {
    try {
      this.record(type, this.sanitizeError(error), 'error');
    } catch {
      this.record(type, { message: 'Unable to serialize error' }, 'error');
    }
  }

  markIncident(
    type: string,
    data: Record<string, unknown> = {},
    mediaSessionId: string | null = this.mediaSessionId
  ): void {
    this.runSafely(() => this.markIncidentInternal(type, data, mediaSessionId));
  }

  private markIncidentInternal(
    type: string,
    data: Record<string, unknown>,
    mediaSessionId: string | null
  ): void {
    if (!this.activeDocument) return;
    const now = Date.now();
    const previousIncidentAt = this.lastIncidentEpochMs.get(type) ?? 0;
    if (now - previousIncidentAt < this.incidentDeduplicationMs) return;
    this.lastIncidentEpochMs.set(type, now);
    this.activeDocument.incidentSamples.push(...this.rollingSamples);
    this.activeDocument.incidentSamples = this.deduplicateSamples(
      this.activeDocument.incidentSamples
    ).slice(-this.incidentSampleLimit);
    this.detailedUntilEpochMs =
      Date.now() + this.detailedCollectionAfterIncidentMs;
    this.record(type, data, 'warning', undefined, mediaSessionId);
    this.schedulePersistence();
  }

  observeRtcSession(session: ObservableRtcSession | null): void {
    this.runSafely(() => this.observeRtcSessionInternal(session));
  }

  private observeRtcSessionInternal(
    session: ObservableRtcSession | null
  ): void {
    if (!session || this.observedSessions.has(session)) return;
    this.observedSessions.add(session);
    if (session.connection) {
      const mediaSessionId = this.observePeerConnection(session.connection);
      this.rtcSessionMediaSessionIds.set(session, mediaSessionId);
    }

    const events = [
      'connecting',
      'progress',
      'accepted',
      'confirmed',
      'ended',
      'failed',
      'hold',
      'unhold',
      'muted',
      'unmuted',
      'reinvite',
      'update',
      'refer',
      'replaces',
      'getusermediafailed',
      'peerconnection:createofferfailed',
      'peerconnection:createanswerfailed',
      'peerconnection:setlocaldescriptionfailed',
      'peerconnection:setremotedescriptionfailed',
    ];

    events.forEach(eventName => {
      session.on(eventName, (event: unknown) => {
        this.runSafely(() => {
          const isError = eventName.includes('failed');
          const sessionMediaSessionId =
            this.rtcSessionMediaSessionIds.get(session) ?? this.mediaSessionId;
          this.record(
            `RTC_SESSION_${eventName.replace(/[^a-z0-9]+/gi, '_').toUpperCase()}`,
            isError ? this.sanitizeError(event) : {},
            isError ? 'error' : 'info',
            undefined,
            sessionMediaSessionId
          );
          if (eventName === 'ended' || eventName === 'failed') {
            this.closeMediaSession(
              sessionMediaSessionId,
              `RTC_SESSION_${eventName.toUpperCase()}`
            );
            this.beginMediaInterruption(
              sessionMediaSessionId,
              'RTC_SESSION_STATE',
              eventName
            );
          }
        });
      });
    });

    session.on('sdp', event => {
      this.runSafely(() => {
        const sessionMediaSessionId =
          this.rtcSessionMediaSessionIds.get(session) ?? this.mediaSessionId;
        this.record(
          'RTC_SESSION_SDP',
          this.summarizeSdp(
            event as { originator?: string; type?: string; sdp?: string }
          ),
          'info',
          undefined,
          sessionMediaSessionId
        );
      });
    });
    session.on('peerconnection', event => {
      this.runSafely(() => {
        const peerConnectionEvent = event as {
          peerconnection?: RTCPeerConnection;
        };
        if (peerConnectionEvent.peerconnection) {
          const mediaSessionId = this.observePeerConnection(
            peerConnectionEvent.peerconnection
          );
          this.rtcSessionMediaSessionIds.set(session, mediaSessionId);
        }
      });
    });
  }

  observePeerConnection(pc: RTCPeerConnection): string {
    try {
      return this.observePeerConnectionInternal(pc);
    } catch {
      const fallbackMediaSessionId =
        this.peerConnectionMediaSessionIds.get(pc) ??
        this.mediaSessionId ??
        this.uuid();
      this.peerConnectionMediaSessionIds.set(pc, fallbackMediaSessionId);
      return fallbackMediaSessionId;
    }
  }

  private observePeerConnectionInternal(pc: RTCPeerConnection): string {
    const existingMediaSessionId = this.peerConnectionMediaSessionIds.get(pc);
    if (existingMediaSessionId) return existingMediaSessionId;
    this.observedPeerConnections.add(pc);
    const mediaSessionId = this.uuid();
    this.peerConnectionMediaSessionIds.set(pc, mediaSessionId);
    this.activateMediaSession(mediaSessionId);

    const stateEvents: Array<[string, string, () => unknown]> = [
      [
        'connectionstatechange',
        'PC_CONNECTION_STATE_CHANGED',
        () => pc.connectionState,
      ],
      [
        'iceconnectionstatechange',
        'ICE_CONNECTION_STATE_CHANGED',
        () => pc.iceConnectionState,
      ],
      [
        'icegatheringstatechange',
        'ICE_GATHERING_STATE_CHANGED',
        () => pc.iceGatheringState,
      ],
      [
        'signalingstatechange',
        'SIGNALING_STATE_CHANGED',
        () => pc.signalingState,
      ],
    ];
    stateEvents.forEach(([browserEvent, diagnosticEvent, getState]) => {
      pc.addEventListener(browserEvent, () => {
        this.runSafely(() => {
          const state = String(getState());
          this.record(
            diagnosticEvent,
            { state },
            state === 'failed' || state === 'disconnected' || state === 'closed'
              ? 'warning'
              : 'info',
            undefined,
            mediaSessionId
          );
          this.handleMediaConnectionState(
            mediaSessionId,
            diagnosticEvent,
            state
          );
        });
      });
    });
    pc.addEventListener('negotiationneeded', () =>
      this.record('NEGOTIATION_NEEDED', {}, 'info', undefined, mediaSessionId)
    );
    pc.addEventListener('icecandidateerror', event => {
      const candidateError = event as RTCPeerConnectionIceErrorEvent;
      this.record(
        'ICE_CANDIDATE_ERROR',
        {
          errorCode: candidateError.errorCode,
          errorText: candidateError.errorText,
        },
        'warning',
        undefined,
        mediaSessionId
      );
    });
    pc.addEventListener('track', event =>
      this.observeTrack(event.track, 'REMOTE', mediaSessionId)
    );
    this.observeLocalTracks(pc);
    this.handleMediaConnectionState(
      mediaSessionId,
      'PEER_CONNECTION_OBSERVED',
      pc.connectionState
    );
    this.handleMediaConnectionState(
      mediaSessionId,
      'ICE_CONNECTION_OBSERVED',
      pc.iceConnectionState
    );
    return mediaSessionId;
  }

  observeLocalTracks(pc: RTCPeerConnection): void {
    this.runSafely(() => this.observeLocalTracksInternal(pc));
  }

  private observeLocalTracksInternal(pc: RTCPeerConnection): void {
    const mediaSessionId =
      this.peerConnectionMediaSessionIds.get(pc) ?? this.mediaSessionId;
    pc.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') {
        this.observeTrack(sender.track, 'LOCAL', mediaSessionId);
      }
    });
  }

  observeAudioElement(audio: HTMLAudioElement): void {
    this.runSafely(() => this.observeAudioElementInternal(audio));
  }

  private observeAudioElementInternal(audio: HTMLAudioElement): void {
    if (this.observedAudioElements.has(audio)) return;
    this.observedAudioElements.add(audio);
    const events = [
      'playing',
      'pause',
      'waiting',
      'stalled',
      'suspend',
      'emptied',
      'ended',
      'error',
    ];
    events.forEach(eventName => {
      audio.addEventListener(eventName, () => {
        this.record(
          `REMOTE_AUDIO_${eventName.toUpperCase()}`,
          {
            paused: audio.paused,
            muted: audio.muted,
            volume: audio.volume,
            readyState: audio.readyState,
            networkState: audio.networkState,
            mediaErrorCode: audio.error?.code ?? null,
          },
          eventName === 'error' || eventName === 'stalled' ? 'warning' : 'debug'
        );
      });
    });
  }

  observeAudioContext(audioContext: AudioContext): void {
    this.runSafely(() => this.observeAudioContextInternal(audioContext));
  }

  private observeAudioContextInternal(audioContext: AudioContext): void {
    if (this.observedAudioContexts.has(audioContext)) return;
    this.observedAudioContexts.add(audioContext);
    audioContext.addEventListener('statechange', () => {
      this.record(
        'AUDIO_CONTEXT_STATE_CHANGED',
        {
          state: audioContext.state,
        },
        audioContext.state === 'suspended' ? 'warning' : 'info'
      );
    });
  }

  recordConnectionSnapshot(pc: RTCPeerConnection): void {
    this.runSafely(() => this.recordConnectionSnapshotInternal(pc));
  }

  private recordConnectionSnapshotInternal(pc: RTCPeerConnection): void {
    const mediaSessionId =
      this.peerConnectionMediaSessionIds.get(pc) ?? this.mediaSessionId;
    this.record(
      'PEER_CONNECTION_SNAPSHOT',
      {
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
        signalingState: pc.signalingState,
        localAudioSenders: pc
          .getSenders()
          .filter(sender => sender.track?.kind === 'audio').length,
        remoteAudioReceivers: pc
          .getReceivers()
          .filter(receiver => receiver.track?.kind === 'audio').length,
      },
      'info',
      undefined,
      mediaSessionId
    );
  }

  recordWebRtcStats(stats: RTCStatsReport, pc: RTCPeerConnection): void {
    if (!this.activeDocument) return;
    try {
      const sample = this.createRtpSample(stats, pc);
      this.rollingSamples.push(sample);
      this.rollingSamples = this.rollingSamples.slice(-this.rollingSampleLimit);
      this.windowSamples.push(sample);

      if (Date.now() <= this.detailedUntilEpochMs) {
        this.activeDocument.incidentSamples.push(sample);
        this.activeDocument.incidentSamples =
          this.activeDocument.incidentSamples.slice(-this.incidentSampleLimit);
        this.documentVersion++;
      }

      if (
        this.windowSamples.length > 1 &&
        this.windowSamples.at(-1)!.offsetMs - this.windowSamples[0].offsetMs >=
          this.metricWindowMs
      ) {
        this.flushMetricWindow();
      }

      this.detectMediaChanges(sample);
      this.detectRtpGaps(sample);
    } catch {
      // Diagnostics must never interfere with the existing RTP monitor.
    }
  }

  private observeTrack(
    track: MediaStreamTrack,
    direction: 'LOCAL' | 'REMOTE',
    mediaSessionId: string | null
  ): void {
    if (this.observedTracks.has(track)) return;
    this.observedTracks.add(track);
    this.record(
      `${direction}_AUDIO_TRACK_ADDED`,
      {
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      },
      'info',
      undefined,
      mediaSessionId
    );
    ['mute', 'unmute', 'ended'].forEach(eventName => {
      track.addEventListener(eventName, () => {
        this.record(
          `${direction}_TRACK_${eventName.toUpperCase()}`,
          {
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
          },
          eventName === 'ended' ? 'warning' : 'info',
          undefined,
          mediaSessionId
        );
      });
    });
  }

  private createRtpSample(
    stats: RTCStatsReport,
    pc: RTCPeerConnection
  ): DiagnosticRtpSample {
    let inbound: AudioRtcStats | undefined;
    let outbound: AudioRtcStats | undefined;
    let remoteInbound: AudioRtcStats | undefined;
    let localAudioSource: AudioRtcStats | undefined;
    let selectedCandidatePair: AudioRtcStats | undefined;
    let selectedCandidatePairId: string | null = null;

    stats.forEach(report => {
      const rtcStats = report as AudioRtcStats;
      const mediaKind = rtcStats.kind ?? rtcStats.mediaType;
      if (mediaKind === 'audio' && rtcStats.type === 'inbound-rtp')
        inbound = rtcStats;
      if (mediaKind === 'audio' && rtcStats.type === 'outbound-rtp')
        outbound = rtcStats;
      if (mediaKind === 'audio' && rtcStats.type === 'remote-inbound-rtp')
        remoteInbound = rtcStats;
      if (mediaKind === 'audio' && rtcStats.type === 'media-source')
        localAudioSource = rtcStats;
      if (
        rtcStats.type === 'candidate-pair' &&
        rtcStats.state === 'succeeded' &&
        (rtcStats.nominated || rtcStats.selected)
      ) {
        selectedCandidatePairId = rtcStats.id;
        selectedCandidatePair = rtcStats;
      }
      if (rtcStats.type === 'transport' && rtcStats.selectedCandidatePairId) {
        selectedCandidatePairId = rtcStats.selectedCandidatePairId;
      }
    });

    return {
      offsetMs: Math.max(0, Date.now() - this.callStartedEpochMs),
      mediaSessionId:
        this.peerConnectionMediaSessionIds.get(pc) ?? this.mediaSessionId,
      inboundBytes: this.numberOrNull(inbound?.bytesReceived),
      inboundPackets: this.numberOrNull(inbound?.packetsReceived),
      inboundPacketsLost: this.numberOrNull(inbound?.packetsLost),
      inboundJitterMs: this.secondsToMs(inbound?.jitter),
      inboundAudioLevel: this.numberOrNull(inbound?.audioLevel),
      inboundTotalAudioEnergy: this.numberOrNull(inbound?.totalAudioEnergy),
      outboundBytes: this.numberOrNull(outbound?.bytesSent),
      outboundPackets: this.numberOrNull(outbound?.packetsSent),
      outboundAudioLevel: this.numberOrNull(
        localAudioSource?.audioLevel ?? outbound?.audioLevel
      ),
      outboundTotalAudioEnergy: this.numberOrNull(
        localAudioSource?.totalAudioEnergy ?? outbound?.totalAudioEnergy
      ),
      remoteInboundPacketsLost: this.numberOrNull(remoteInbound?.packetsLost),
      remoteInboundJitterMs: this.secondsToMs(remoteInbound?.jitter),
      roundTripTimeMs: this.secondsToMs(
        remoteInbound?.roundTripTime ??
          selectedCandidatePair?.currentRoundTripTime
      ),
      inboundSsrc: this.numberOrNull(inbound?.ssrc),
      outboundSsrc: this.numberOrNull(outbound?.ssrc),
      selectedCandidatePairId,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
    };
  }

  private activateMediaSession(mediaSessionId: string): void {
    const previousMediaSessionId = this.mediaSessionId;
    if (this.activeDocument) this.flushMetricWindow();
    this.mediaSessionId = mediaSessionId;
    this.resetRtpProgress();
    this.rollingSamples = [];
    this.windowSamples = [];
    if (!this.activeDocument) return;

    this.closeOpenMediaSession('REPLACED_BY_NEW_PEER_CONNECTION');
    this.activeDocument.mediaSessionId = mediaSessionId;
    this.activeDocument.mediaSessions.push(
      this.createMediaSession(mediaSessionId, previousMediaSessionId, false)
    );
    this.record(
      previousMediaSessionId
        ? 'MEDIA_SESSION_REPLACED'
        : 'MEDIA_SESSION_STARTED',
      { previousMediaSessionId },
      previousMediaSessionId ? 'warning' : 'info',
      undefined,
      mediaSessionId
    );
  }

  private createMediaSession(
    mediaSessionId: string,
    previousMediaSessionId: string | null,
    startedBeforeCall: boolean
  ): DiagnosticMediaSession {
    const now = new Date().toISOString();
    return {
      mediaSessionId,
      previousMediaSessionId,
      startedAt: now,
      startedOffsetMs: Math.max(0, Date.now() - this.callStartedEpochMs),
      startedBeforeCall,
      endedAt: null,
      endedOffsetMs: null,
      endReason: null,
    };
  }

  private closeOpenMediaSession(reason: string): void {
    const openSession = this.activeDocument?.mediaSessions
      .filter(session => !session.endedAt)
      .at(-1);
    if (openSession) this.closeMediaSession(openSession.mediaSessionId, reason);
  }

  private closeMediaSession(
    mediaSessionId: string | null,
    reason: string
  ): void {
    if (!this.activeDocument || !mediaSessionId) return;
    const session = this.activeDocument.mediaSessions.find(
      item => item.mediaSessionId === mediaSessionId && !item.endedAt
    );
    if (!session) return;
    const endedEpochMs = Date.now();
    session.endedAt = new Date(endedEpochMs).toISOString();
    session.endedOffsetMs = Math.max(0, endedEpochMs - this.callStartedEpochMs);
    session.endReason = reason;
    this.activeDocument.updatedAt = session.endedAt;
    this.documentVersion++;
  }

  private handleMediaConnectionState(
    mediaSessionId: string,
    source: string,
    state: string
  ): void {
    if (state === 'failed' || state === 'disconnected' || state === 'closed') {
      this.beginMediaInterruption(mediaSessionId, source, state);
      if (state === 'failed' || state === 'closed') {
        this.closeMediaSession(mediaSessionId, `${source}_${state}`);
      }
      return;
    }
    if (state === 'connected' || state === 'completed') {
      this.recoverMediaInterruption(mediaSessionId, source, state);
    }
  }

  private beginMediaInterruption(
    mediaSessionId: string | null,
    source: string,
    state: string
  ): void {
    if (
      !this.activeDocument?.answeredAt ||
      (mediaSessionId &&
        this.activeDocument.mediaSessionId &&
        mediaSessionId !== this.activeDocument.mediaSessionId) ||
      this.mediaInterruption
    ) {
      return;
    }
    const startedEpochMs = Date.now();
    this.mediaInterruption = {
      mediaSessionId,
      startedAt: new Date(startedEpochMs).toISOString(),
      startedEpochMs,
      source,
      state,
      suspectedDeadAirRecorded: false,
    };
    this.markIncident(
      'MEDIA_CONNECTION_INTERRUPTED',
      { source, state },
      mediaSessionId
    );
    this.mediaInterruptionTimer = setTimeout(
      () => this.emitSuspectedConnectionDeadAir(),
      this.suspectedDeadAirThresholdMs
    );
  }

  private emitSuspectedConnectionDeadAir(): void {
    const interruption = this.mediaInterruption;
    if (!interruption || interruption.suspectedDeadAirRecorded) return;
    const durationMs = Date.now() - interruption.startedEpochMs;
    if (durationMs < this.suspectedDeadAirThresholdMs) return;
    interruption.suspectedDeadAirRecorded = true;
    this.markIncident(
      'SUSPECTED_BIDIRECTIONAL_DEAD_AIR',
      {
        source: interruption.source,
        state: interruption.state,
        interruptionStartedAt: interruption.startedAt,
        durationMs,
      },
      interruption.mediaSessionId
    );
  }

  private recoverMediaInterruption(
    recoveredMediaSessionId: string,
    source: string,
    state: string
  ): void {
    const interruption = this.mediaInterruption;
    if (!interruption) return;
    this.emitSuspectedConnectionDeadAir();
    const recoveredAtEpochMs = Date.now();
    this.record(
      'MEDIA_CONNECTION_RECOVERED',
      {
        source,
        state,
        interruptedMediaSessionId: interruption.mediaSessionId,
        interruptionStartedAt: interruption.startedAt,
        durationMs: recoveredAtEpochMs - interruption.startedEpochMs,
        suspectedDeadAir: interruption.suspectedDeadAirRecorded,
      },
      'info',
      undefined,
      recoveredMediaSessionId
    );
    this.clearMediaInterruption();
  }

  private clearMediaInterruption(): void {
    if (this.mediaInterruptionTimer) {
      clearTimeout(this.mediaInterruptionTimer);
      this.mediaInterruptionTimer = undefined;
    }
    this.mediaInterruption = null;
  }

  private detectRtpGaps(sample: DiagnosticRtpSample): void {
    if (
      !this.activeDocument?.answeredAt ||
      sample.connectionState !== 'connected' ||
      (sample.iceConnectionState !== 'connected' &&
        sample.iceConnectionState !== 'completed')
    ) {
      return;
    }
    this.updateRtpProgress(
      'INBOUND',
      this.inboundProgress,
      sample.inboundBytes,
      sample.inboundPackets,
      sample
    );
    this.updateRtpProgress(
      'OUTBOUND',
      this.outboundProgress,
      sample.outboundBytes,
      sample.outboundPackets,
      sample
    );
  }

  private updateRtpProgress(
    direction: 'INBOUND' | 'OUTBOUND',
    progress: RtpProgressState,
    bytes: number | null,
    packets: number | null,
    sample: DiagnosticRtpSample
  ): void {
    if (bytes === null && packets === null) return;
    const countersReset =
      (bytes !== null &&
        progress.lastBytes !== null &&
        bytes < progress.lastBytes) ||
      (packets !== null &&
        progress.lastPackets !== null &&
        packets < progress.lastPackets);
    const advanced =
      (bytes !== null &&
        progress.lastBytes !== null &&
        bytes > progress.lastBytes) ||
      (packets !== null &&
        progress.lastPackets !== null &&
        packets > progress.lastPackets);
    const hasBaseline =
      progress.lastBytes !== null || progress.lastPackets !== null;

    if (!hasBaseline || countersReset || advanced) {
      if (progress.gapRecorded && progress.stalledSinceOffsetMs !== null) {
        this.record(
          `${direction}_RTP_RECOVERED`,
          { durationMs: sample.offsetMs - progress.stalledSinceOffsetMs },
          'info',
          undefined,
          sample.mediaSessionId
        );
      }
      progress.stalledSinceOffsetMs = null;
      progress.gapRecorded = false;
    } else {
      progress.stalledSinceOffsetMs ??= progress.lastOffsetMs;
      const durationMs =
        sample.offsetMs - (progress.stalledSinceOffsetMs ?? sample.offsetMs);
      if (
        durationMs >= this.suspectedDeadAirThresholdMs &&
        !progress.gapRecorded
      ) {
        progress.gapRecorded = true;
        this.markIncident(
          `SUSPECTED_${direction}_RTP_GAP`,
          { durationMs, bytes, packets },
          sample.mediaSessionId
        );
      }
    }

    progress.lastBytes = bytes;
    progress.lastPackets = packets;
    progress.lastOffsetMs = sample.offsetMs;
  }

  private resetRtpProgress(): void {
    this.inboundProgress = this.emptyRtpProgressState();
    this.outboundProgress = this.emptyRtpProgressState();
  }

  private emptyRtpProgressState(): RtpProgressState {
    return {
      lastBytes: null,
      lastPackets: null,
      lastOffsetMs: null,
      stalledSinceOffsetMs: null,
      gapRecorded: false,
    };
  }

  private detectMediaChanges(sample: DiagnosticRtpSample): void {
    if (
      this.lastCandidatePairId &&
      sample.selectedCandidatePairId &&
      this.lastCandidatePairId !== sample.selectedCandidatePairId
    ) {
      this.record(
        'SELECTED_CANDIDATE_PAIR_CHANGED',
        {},
        'warning',
        undefined,
        sample.mediaSessionId
      );
    }
    if (
      this.lastInboundSsrc &&
      sample.inboundSsrc &&
      this.lastInboundSsrc !== sample.inboundSsrc
    ) {
      this.record(
        'INBOUND_SSRC_CHANGED',
        {},
        'warning',
        undefined,
        sample.mediaSessionId
      );
    }
    if (
      this.lastOutboundSsrc &&
      sample.outboundSsrc &&
      this.lastOutboundSsrc !== sample.outboundSsrc
    ) {
      this.record(
        'OUTBOUND_SSRC_CHANGED',
        {},
        'warning',
        undefined,
        sample.mediaSessionId
      );
    }
    this.lastCandidatePairId =
      sample.selectedCandidatePairId ?? this.lastCandidatePairId;
    this.lastInboundSsrc = sample.inboundSsrc ?? this.lastInboundSsrc;
    this.lastOutboundSsrc = sample.outboundSsrc ?? this.lastOutboundSsrc;
  }

  private flushMetricWindow(): void {
    if (!this.activeDocument || this.windowSamples.length === 0) return;
    const first = this.windowSamples[0];
    const last = this.windowSamples.at(-1)!;
    const window: DiagnosticMetricWindow = {
      startedOffsetMs: first.offsetMs,
      endedOffsetMs: last.offsetMs,
      mediaSessionId: last.mediaSessionId,
      samples: this.windowSamples.length,
      inboundBytesDelta: this.delta(first.inboundBytes, last.inboundBytes),
      inboundPacketsDelta: this.delta(
        first.inboundPackets,
        last.inboundPackets
      ),
      outboundBytesDelta: this.delta(first.outboundBytes, last.outboundBytes),
      outboundPacketsDelta: this.delta(
        first.outboundPackets,
        last.outboundPackets
      ),
      maxInboundJitterMs: this.max(
        this.windowSamples.map(s => s.inboundJitterMs)
      ),
      maxRoundTripTimeMs: this.max(
        this.windowSamples.map(s => s.roundTripTimeMs)
      ),
      inboundPacketsLostDelta: this.delta(
        first.inboundPacketsLost,
        last.inboundPacketsLost
      ),
    };
    this.activeDocument.metricWindows.push(window);
    this.activeDocument.metricWindows = this.activeDocument.metricWindows.slice(
      -this.metricWindowLimit
    );
    this.activeDocument.updatedAt = new Date().toISOString();
    this.documentVersion++;
    this.windowSamples = [last];
  }

  private startPersistenceTimer(): void {
    this.stopPersistenceTimer();
    this.persistTimer = setInterval(
      () => this.schedulePersistence(),
      this.persistIntervalMs
    );
  }

  private stopPersistenceTimer(): void {
    if (!this.persistTimer) return;
    clearInterval(this.persistTimer);
    this.persistTimer = undefined;
  }

  private schedulePersistence(): void {
    if (this.persistenceScheduled || !this.activeDocument) return;
    this.persistenceScheduled = true;
    const persist = () => {
      this.persistenceScheduled = false;
      this.persistActiveDocument();
    };

    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(persist, { timeout: 2_000 });
    } else {
      setTimeout(persist, 0);
    }
  }

  private persistActiveDocument(force = false): void {
    const activeDiagnosticId = this.activeDocument?.diagnosticId;
    if (!activeDiagnosticId) return;
    if (
      !force &&
      this.documentVersion <=
        (this.persistedVersions.get(activeDiagnosticId) ?? -1)
    ) {
      return;
    }
    let snapshot: CallDiagnosticDocument | null = null;
    try {
      if (this.activeDocument) {
        this.activeDocument.checkpointSequence = Math.max(
          this.activeDocument.checkpointSequence,
          this.documentVersion
        );
        if (
          this.activeDocument.checkpointSequence >
          this.activeDocument.uploadState.lastUploadedCheckpointSequence
        ) {
          this.activeDocument.uploadStatus = 'PENDING';
        }
      }
      snapshot = this.activeDocument
        ? structuredClone(this.activeDocument)
        : null;
    } catch {
      return;
    }
    if (!snapshot) return;
    const snapshotVersion = this.documentVersion;

    this.openDatabase()
      .then(database => {
        if (!database) return;
        const transaction = database.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).put(snapshot);
        transaction.oncomplete = () => {
          this.persistedVersions.set(
            snapshot.diagnosticId,
            Math.max(
              this.persistedVersions.get(snapshot.diagnosticId) ?? -1,
              snapshotVersion
            )
          );
          if (snapshot.isFinal) {
            this.scheduleUpload();
          }
        };
      })
      .catch(() => undefined);
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (this.databasePromise) return this.databasePromise;
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);

    this.databasePromise = new Promise(resolve => {
      let settled = false;
      const request = indexedDB.open(this.databaseName, 4);
      request.onupgradeneeded = () => {
        const database = request.result;
        let store: IDBObjectStore;
        if (!database.objectStoreNames.contains(this.storeName)) {
          store = database.createObjectStore(this.storeName, {
            keyPath: 'diagnosticId',
          });
        } else {
          store = request.transaction!.objectStore(this.storeName);
        }
        if (!store.indexNames.contains(this.tenantUploadIndexName)) {
          store.createIndex(
            this.tenantUploadIndexName,
            ['tenantId', 'uploadStatus'],
            { unique: false }
          );
        }
        if (!store.indexNames.contains(this.retentionIndexName)) {
          store.createIndex(this.retentionIndexName, 'retentionExpiresAt', {
            unique: false,
          });
        }

        const cursorRequest = store.openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) return;
          const document = this.withUploadDefaults(
            cursor.value as CallDiagnosticDocument
          );
          cursor.update(document);
          cursor.continue();
        };
      };
      request.onsuccess = () => {
        const database = request.result;
        if (settled) {
          database.close();
          return;
        }
        settled = true;
        database.onversionchange = () => {
          database.close();
          this.databasePromise = undefined;
        };
        resolve(database);
      };
      request.onerror = () => {
        if (settled) return;
        settled = true;
        this.databasePromise = undefined;
        resolve(null);
      };
      request.onblocked = () => {
        if (settled) return;
        settled = true;
        // A different tab may temporarily hold an older connection. Allow a
        // later persistence/upload cycle to retry instead of caching failure.
        this.databasePromise = undefined;
        resolve(null);
      };
    });
    return this.databasePromise;
  }

  private scheduleExpiredDocumentCleanup(): void {
    const cleanup = () => void this.cleanupExpiredDocuments();
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(cleanup, { timeout: 5_000 });
    } else {
      setTimeout(cleanup, 0);
    }
  }

  private async cleanupExpiredDocuments(): Promise<void> {
    try {
      const database = await this.openDatabase();
      if (!database) return;
      const activeDiagnosticId = this.activeDocument?.diagnosticId;
      await new Promise<void>(resolve => {
        const transaction = database.transaction(this.storeName, 'readwrite');
        const request = transaction
          .objectStore(this.storeName)
          .index(this.retentionIndexName)
          .openCursor(IDBKeyRange.upperBound(new Date().toISOString()));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          if (cursor.primaryKey !== activeDiagnosticId) {
            cursor.delete();
          }
          cursor.continue();
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      });
    } catch {
      // Retention cleanup is best effort and must never affect call handling.
    }
  }

  private scheduleUpload(preferKeepalive = false): void {
    this.keepaliveRequested ||= preferKeepalive;
    if (this.uploadInProgress) {
      this.uploadRequestedAfterCurrent = true;
      return;
    }
    if (this.uploadScheduled) return;
    this.uploadScheduled = true;
    setTimeout(() => {
      this.uploadScheduled = false;
      const useKeepalive = this.keepaliveRequested;
      this.keepaliveRequested = false;
      void this.uploadPendingDocuments(useKeepalive);
    }, 0);
  }

  private async uploadPendingDocuments(
    preferKeepalive: boolean
  ): Promise<void> {
    if (this.uploadInProgress) {
      this.uploadRequestedAfterCurrent = true;
      this.keepaliveRequested ||= preferKeepalive;
      return;
    }

    const authContext = this.getUploadAuthContext();
    if (!authContext || typeof fetch === 'undefined') return;

    this.uploadInProgress = true;
    try {
      await this.claimUnattributedDocuments(
        authContext.organizationId,
        authContext.agentId
      );
      const documents = await this.readPendingDocuments(
        authContext.organizationId
      );
      const now = Date.now();
      const pending = documents
        .filter(
          document =>
            document.checkpointSequence >
            document.uploadState.lastUploadedCheckpointSequence
        )
        .filter(document => {
          const nextAttemptEpochMs = document.uploadState.nextAttemptAt
            ? Date.parse(document.uploadState.nextAttemptAt)
            : Number.NaN;
          return (
            !document.uploadState.nextAttemptAt ||
            !Number.isFinite(nextAttemptEpochMs) ||
            nextAttemptEpochMs <= now
          );
        })
        .sort((left, right) => left.startedAt.localeCompare(right.startedAt));

      for (const document of pending.slice(0, this.uploadBatchLimit)) {
        await this.uploadDocument(document, authContext.token, preferKeepalive);
      }

      if (pending.length > this.uploadBatchLimit) {
        this.uploadRequestedAfterCurrent = true;
      }
    } catch {
      // The interval or next login will retry; never surface telemetry failures.
    } finally {
      this.uploadInProgress = false;
      if (this.uploadRequestedAfterCurrent) {
        this.uploadRequestedAfterCurrent = false;
        this.scheduleUpload(this.keepaliveRequested);
      }
    }
  }

  private async uploadDocument(
    document: CallDiagnosticDocument,
    token: string,
    preferKeepalive: boolean
  ): Promise<void> {
    const request = this.buildUploadRequest(document);
    const body = JSON.stringify(request);
    const bodyBytes =
      typeof TextEncoder === 'undefined'
        ? body.length * 2
        : new TextEncoder().encode(body).byteLength;
    const keepalive =
      preferKeepalive && bodyBytes <= this.maxKeepaliveBodyBytes;
    const controller = keepalive ? null : new AbortController();
    const timeout = controller
      ? setTimeout(() => controller.abort(), this.uploadTimeoutMs)
      : undefined;

    try {
      const response = await fetch(Core_URL.CALL_DIAGNOSTICS_UPSERT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Kunnect-Sid': String(
            this.storage.getItem(Storagekeys.sessionId, true) ?? ''
          ),
          'X-Service-id': String(
            this.storage.getItem(Storagekeys.orgId, true) ?? ''
          ),
        },
        body,
        keepalive,
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      await this.updateUploadState(document, true);
    } catch {
      await this.updateUploadState(document, false);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  private buildUploadRequest(
    document: CallDiagnosticDocument
  ): CallDiagnosticsUpsertRequest {
    const payloadWithoutLocalState: Partial<CallDiagnosticDocument> =
      structuredClone(document);
    delete payloadWithoutLocalState.uploadState;
    delete payloadWithoutLocalState.uploadStatus;
    delete payloadWithoutLocalState.retentionExpiresAt;
    const payload = payloadWithoutLocalState as Omit<
      CallDiagnosticDocument,
      'uploadState' | 'uploadStatus' | 'retentionExpiresAt'
    >;
    const deadAirEvents = document.events.filter(event =>
      /DEAD_AIR|RTP_NO_PROGRESS|RTP_GAP/.test(event.type)
    );
    return {
      schemaVersion: document.schemaVersion,
      checkpointSequence: document.checkpointSequence,
      diagnosticId: document.diagnosticId,
      callId: document.callId,
      sipCallId: document.sipCallId,
      callType: document.callType,
      agentId: document.agentId,
      clientSessionId: document.clientSessionId,
      browserTabId: document.browserTabId,
      mediaSessionId: document.mediaSessionId,
      startedAt: document.startedAt,
      answeredAt: document.answeredAt,
      endedAt: document.endedAt,
      isFinal: document.isFinal,
      transferCallId: document.transferCallId,
      transferCount: document.transfers.length,
      deadAirDetected: deadAirEvents.length > 0,
      deadAirIncidentCount: deadAirEvents.length,
      payload,
    };
  }

  private getUploadAuthContext(): {
    token: string;
    organizationId: string;
    agentId: string | null;
  } | null {
    try {
      const token = this.getStoredString(Storagekeys.token);
      const organizationId = this.getStoredString(Storagekeys.orgId);
      const agentId = this.getStoredString(Storagekeys.userId);
      return token && organizationId
        ? { token, organizationId, agentId }
        : null;
    } catch {
      return null;
    }
  }

  private async claimUnattributedDocuments(
    organizationId: string,
    agentId: string | null
  ): Promise<void> {
    const clientSessionId = this.getClientSessionId();
    const ownershipContext = `${organizationId}:${clientSessionId}`;
    if (this.claimedOwnershipContext === ownershipContext) return;
    const database = await this.openDatabase();
    if (!database) return;
    const completed = await new Promise<boolean>(resolve => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const document = this.withUploadDefaults(
          cursor.value as CallDiagnosticDocument
        );
        if (
          document.uploadStatus === 'PENDING' &&
          !this.nonEmptyString(document.tenantId) &&
          document.clientSessionId === clientSessionId
        ) {
          document.tenantId = organizationId;
          document.agentId ??= agentId;
          cursor.update(document);
        }
        cursor.continue();
      };
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => resolve(false);
      transaction.onabort = () => resolve(false);
    });
    if (completed) this.claimedOwnershipContext = ownershipContext;
  }

  private async readPendingDocuments(
    organizationId: string
  ): Promise<CallDiagnosticDocument[]> {
    const database = await this.openDatabase();
    if (!database) return [];
    return new Promise(resolve => {
      const transaction = database.transaction(this.storeName, 'readonly');
      const request = transaction
        .objectStore(this.storeName)
        .index(this.tenantUploadIndexName)
        .getAll(
          IDBKeyRange.only([organizationId, 'PENDING']),
          this.uploadBatchLimit + 1
        );
      request.onsuccess = () =>
        resolve(
          (request.result as CallDiagnosticDocument[]).map(document =>
            this.withUploadDefaults(document)
          )
        );
      request.onerror = () => resolve([]);
      transaction.onabort = () => resolve([]);
    });
  }

  private async updateUploadState(
    uploadedDocument: CallDiagnosticDocument,
    succeeded: boolean
  ): Promise<void> {
    const now = new Date();
    const attempts = succeeded
      ? 0
      : uploadedDocument.uploadState.attemptCount + 1;
    const nextAttemptAt = succeeded
      ? null
      : new Date(
          now.getTime() +
            Math.min(5 * 60_000, 15_000 * 2 ** Math.min(attempts - 1, 5))
        ).toISOString();

    if (this.activeDocument?.diagnosticId === uploadedDocument.diagnosticId) {
      const activeUploadComplete =
        succeeded &&
        this.activeDocument.checkpointSequence <=
          uploadedDocument.checkpointSequence;
      this.activeDocument.uploadState = {
        lastUploadedCheckpointSequence: succeeded
          ? Math.max(
              this.activeDocument.uploadState.lastUploadedCheckpointSequence,
              uploadedDocument.checkpointSequence
            )
          : this.activeDocument.uploadState.lastUploadedCheckpointSequence,
        attemptCount: attempts,
        lastAttemptAt: now.toISOString(),
        nextAttemptAt,
      };
      this.activeDocument.uploadStatus = activeUploadComplete
        ? 'UPLOADED'
        : 'PENDING';
    }

    const database = await this.openDatabase();
    if (!database) return;
    await new Promise<void>(resolve => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(uploadedDocument.diagnosticId);
      getRequest.onsuccess = () => {
        const latest = getRequest.result
          ? this.withUploadDefaults(getRequest.result as CallDiagnosticDocument)
          : uploadedDocument;
        const finalUploadComplete =
          succeeded &&
          uploadedDocument.isFinal &&
          latest.isFinal &&
          latest.checkpointSequence <= uploadedDocument.checkpointSequence;
        if (finalUploadComplete) {
          store.delete(uploadedDocument.diagnosticId);
          return;
        }
        latest.uploadState = {
          lastUploadedCheckpointSequence: succeeded
            ? Math.max(
                latest.uploadState.lastUploadedCheckpointSequence,
                uploadedDocument.checkpointSequence
              )
            : latest.uploadState.lastUploadedCheckpointSequence,
          attemptCount: attempts,
          lastAttemptAt: now.toISOString(),
          nextAttemptAt,
        };
        latest.uploadStatus =
          succeeded &&
          latest.checkpointSequence <= uploadedDocument.checkpointSequence
            ? 'UPLOADED'
            : 'PENDING';
        store.put(latest);
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  }

  private withUploadDefaults(
    document: CallDiagnosticDocument
  ): CallDiagnosticDocument {
    document.schemaVersion = 2;
    document.checkpointSequence ??=
      document.events.length + document.metricWindows.length + 1;
    document.uploadStatus ??= 'PENDING';
    document.uploadState ??= {
      lastUploadedCheckpointSequence: -1,
      attemptCount: 0,
      lastAttemptAt: null,
      nextAttemptAt: null,
    };
    document.preCallEvents ??= [];
    document.mediaSessions ??= document.mediaSessionId
      ? [
          {
            mediaSessionId: document.mediaSessionId,
            previousMediaSessionId: null,
            startedAt: document.startedAt,
            startedOffsetMs: 0,
            startedBeforeCall: true,
            endedAt: document.endedAt,
            endedOffsetMs: document.endedAt
              ? Math.max(
                  0,
                  Date.parse(document.endedAt) - Date.parse(document.startedAt)
                )
              : null,
            endReason: document.isFinal ? 'LEGACY_DOCUMENT_FINALIZED' : null,
          },
        ]
      : [];
    document.events.forEach(event => {
      event.mediaSessionId ??= document.mediaSessionId;
    });
    document.preCallEvents.forEach(event => {
      event.mediaSessionId ??= document.mediaSessionId;
    });
    document.metricWindows.forEach(window => {
      window.mediaSessionId ??= document.mediaSessionId;
    });
    document.incidentSamples.forEach(sample => {
      sample.mediaSessionId ??= document.mediaSessionId;
    });
    if (!document.retentionExpiresAt) {
      const startedAtEpochMs = Date.parse(document.startedAt);
      const retentionStartEpochMs = Number.isFinite(startedAtEpochMs)
        ? startedAtEpochMs
        : Date.now();
      document.retentionExpiresAt = new Date(
        retentionStartEpochMs + this.retentionMs
      ).toISOString();
    }
    return document;
  }

  private runSafely(action: () => void): void {
    try {
      action();
    } catch {
      // Monitoring is strictly observational; failures cannot escape into
      // SIP, USM, media-device, or call-state handlers.
    }
  }

  private summarizeSdp(event: {
    originator?: string;
    type?: string;
    sdp?: string;
  }): Record<string, unknown> {
    const sdp = event.sdp ?? '';
    const codecs = Array.from(
      new Set(
        [...sdp.matchAll(/^a=rtpmap:\d+\s+([^/\s]+)/gim)].map(match => match[1])
      )
    );
    const direction =
      sdp.match(/^a=(sendrecv|sendonly|recvonly|inactive)$/im)?.[1] ?? null;
    const originVersion = sdp.match(/^o=\S+\s+\S+\s+(\S+)/im)?.[1] ?? null;
    return {
      originator: event.originator ?? null,
      type: event.type ?? null,
      codecs,
      direction,
      originVersion,
      audioMediaSections: (sdp.match(/^m=audio\s+/gim) ?? []).length,
      audioRejected: /^m=audio\s+0\s+/im.test(sdp),
    };
  }

  private sanitizeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: this.sanitizeText(error.message),
        stack: this.sanitizeText(
          error.stack?.split('\n').slice(0, 4).join('\n') ?? ''
        ),
      };
    }
    return { message: this.sanitizeText(String(error ?? 'Unknown error')) };
  }

  private sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(data)
        .filter(
          ([key]) =>
            !/(password|token|authorization|phone|contact|rawSdp)/i.test(key)
        )
        .map(([key, value]) => [
          key,
          typeof value === 'string' ? this.sanitizeText(value) : value,
        ])
    );
  }

  private sanitizeText(value: string): string {
    return value
      .replace(
        /([?&](?:token|access_token|refresh_token|authorization)=)[^&\s]+/gi,
        '$1[redacted]'
      )
      .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[ip]')
      .slice(0, 1000);
  }

  private bufferPreCallEvent(
    type: string,
    data: Record<string, unknown>,
    severity: DiagnosticSeverity,
    mediaSessionId: string | null
  ): void {
    const timestampEpochMs = Date.now();
    const oldestAllowedEpochMs = timestampEpochMs - this.preCallWindowMs;
    this.preCallBuffer = this.preCallBuffer.filter(
      event => event.timestampEpochMs >= oldestAllowedEpochMs
    );
    this.preCallBuffer.push({
      eventId: this.uuid(),
      type,
      timestamp: new Date(timestampEpochMs).toISOString(),
      timestampEpochMs,
      mediaSessionId,
      severity,
      data: this.sanitizeData(data),
    });
    this.preCallBuffer = this.preCallBuffer.slice(-this.preCallEventLimit);
  }

  private consumePreCallEvents(
    callStartEpochMs: number
  ): DiagnosticPreCallEvent[] {
    const oldestAllowedEpochMs = callStartEpochMs - this.preCallWindowMs;
    const events = this.preCallBuffer
      .filter(event => event.timestampEpochMs >= oldestAllowedEpochMs)
      .map(event => ({
        eventId: event.eventId,
        type: event.type,
        timestamp: event.timestamp,
        ageMsAtCallStart: Math.max(
          0,
          callStartEpochMs - event.timestampEpochMs
        ),
        mediaSessionId: event.mediaSessionId,
        severity: event.severity,
        data: event.data,
      }));
    this.preCallBuffer = [];
    return events;
  }

  private deduplicateSamples(
    samples: DiagnosticRtpSample[]
  ): DiagnosticRtpSample[] {
    return Array.from(
      new Map(samples.map(sample => [sample.offsetMs, sample])).values()
    );
  }

  private getClientSessionId(): string {
    if (this.cachedClientSessionId) return this.cachedClientSessionId;
    try {
      const existing = localStorage.getItem(this.clientSessionStorageKey);
      if (existing) {
        this.cachedClientSessionId = existing;
        return existing;
      }
      const sessionId = this.uuid();
      localStorage.setItem(this.clientSessionStorageKey, sessionId);
      this.cachedClientSessionId = sessionId;
      return sessionId;
    } catch {
      this.cachedClientSessionId = this.uuid();
      return this.cachedClientSessionId;
    }
  }

  private getStoredString(key: Storagekeys): string | null {
    try {
      return this.nonEmptyString(this.storage.getItem(key, true));
    } catch {
      return null;
    }
  }

  private nonEmptyString(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized || null;
  }

  private uuid(): string {
    try {
      const generated = globalThis.crypto?.randomUUID?.();
      if (generated) return generated;
    } catch {
      // Fall back below when secure UUID generation is unavailable.
    }
    return `diag-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private secondsToMs(value: unknown): number | null {
    const seconds = this.numberOrNull(value);
    return seconds === null ? null : seconds * 1000;
  }

  private delta(first: number | null, last: number | null): number | null {
    return first === null || last === null ? null : Math.max(0, last - first);
  }

  private max(values: Array<number | null>): number | null {
    const numbers = values.filter((value): value is number => value !== null);
    return numbers.length ? Math.max(...numbers) : null;
  }
}
